// Reusable hook bundling all the state + side effects behind the
// "📍 Use my location / 🔍 Search a place" feature.
//
// Owned state:
//   userLocation       { lat, lng, accuracy } | null
//   locationLabel      friendly text shown on the active chip
//                      ("Your location" for GPS, "Sivakasi" for a search)
//   locationSource     'gps' | 'search' — drives chip icon
//   radiusKm           current radius filter (null = "Any")
//   searchOpen / Q / Results / Loading — search dropdown state
//   recentPlaces       persisted recent searches (localStorage)
//   geolocating / geoError — UI state for the GPS button
//
// Provided actions:
//   requestLocation()  fire browser/Capacitor GPS
//   clearLocation()    wipe everything back to the initial bar
//   pickResult(place)  apply a Nominatim hit → location chip active
//   clearRecent()      drop the localStorage recent-search list
//
// Persistence: userLocation / label / source are session-scoped via
// sessionStorage so navigating to a property detail and back keeps
// the chip alive. A fresh tab always starts blank — the user re-opts in.
// Recent searches are cross-session (localStorage).
//
// Two pages use this today: PublicBrowsePage (public marketplace) and
// the tenant's MapViewPage. Adding it to a third surface is now a
// one-line import.

import { useState, useEffect, useRef } from 'react';
import {
  getCurrentLocation,
  searchPlaces,
  summarizePlace,
  getRecentPlaces,
  addRecentPlace,
  clearRecentPlaces,
} from '../utils/geo';

export default function useLocationFilter(opts = {}) {
  // Per-page sessionStorage key so different surfaces don't stomp on
  // each other (e.g., the tenant's tenant Map View shouldn't see the
  // user's public-browse search, and vice versa).
  const { storageKey = 'userLocation', defaultLocationLabel = 'Your location' } = opts;
  const labelKey = `${storageKey}_label`;
  const sourceKey = `${storageKey}_source`;

  const [userLocation, setUserLocation] = useState(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [locationLabel, setLocationLabel] = useState(() => {
    try { return sessionStorage.getItem(labelKey) || null; } catch { return null; }
  });
  const [locationSource, setLocationSource] = useState(() => {
    try { return sessionStorage.getItem(sourceKey) || null; } catch { return null; }
  });
  const [geolocating, setGeolocating] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const [radiusKm, setRadiusKm] = useState(null);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [recentPlaces, setRecentPlaces] = useState(() => getRecentPlaces());
  // Cancels the in-flight Nominatim proxy request when the user keeps
  // typing — without this a slow network would let stale results win.
  const searchAbortRef = useRef(null);

  const persist = (loc, label, source) => {
    try {
      if (loc) sessionStorage.setItem(storageKey, JSON.stringify(loc));
      else sessionStorage.removeItem(storageKey);
      if (label) sessionStorage.setItem(labelKey, label);
      else sessionStorage.removeItem(labelKey);
      if (source) sessionStorage.setItem(sourceKey, source);
      else sessionStorage.removeItem(sourceKey);
    } catch (_) { /* private mode etc. — non-fatal */ }
  };

  const setLocation = (loc, label, source) => {
    setUserLocation(loc);
    setLocationLabel(label);
    setLocationSource(source);
    persist(loc, label, source);
  };

  const requestLocation = async () => {
    setGeolocating(true);
    setGeoError(null);
    try {
      const loc = await getCurrentLocation({ highAccuracy: true, timeoutMs: 10000 });
      setLocation(loc, defaultLocationLabel, 'gps');
    } catch (err) {
      // Store both code + message so the UI can render platform-
      // specific guidance for permission_denied vs other failures.
      setGeoError({
        code: err?.code || 'unknown',
        message: err?.message || 'Could not get your location.',
      });
    } finally {
      setGeolocating(false);
    }
  };

  const clearLocation = () => {
    setUserLocation(null);
    setLocationLabel(null);
    setLocationSource(null);
    setRadiusKm(null);
    setGeoError(null);
    setSearchQ('');
    setSearchResults([]);
    setSearchOpen(false);
    persist(null, null, null);
  };

  const pickResult = (place) => {
    const { title } = summarizePlace(place);
    setLocation(
      { lat: place.lat, lng: place.lng, accuracy: null },
      title || place.display_name,
      'search'
    );
    addRecentPlace(place);
    setRecentPlaces(getRecentPlaces());
    setSearchOpen(false);
    setSearchQ('');
    setSearchResults([]);
  };

  const clearRecent = () => {
    clearRecentPlaces();
    setRecentPlaces([]);
  };

  // Dismisses the geoError so the warning modal closes. Used by the
  // LocationBar's "Try again" and "Search a place" buttons — clicking
  // either should take the modal away even if the underlying browser
  // permission is still denied (retry handles its own clear at the
  // start of requestLocation; this is for the search path).
  const dismissError = () => setGeoError(null);

  // Debounced server-proxied place search. Fires 350ms after the user
  // stops typing, aborts any prior in-flight call so the freshest
  // query always wins. Server-side is rate-limited so this is safe.
  useEffect(() => {
    if (!searchOpen || searchQ.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const id = setTimeout(async () => {
      if (searchAbortRef.current) {
        try { searchAbortRef.current.abort(); } catch (_) {}
      }
      const ctrl = new AbortController();
      searchAbortRef.current = ctrl;
      setSearchLoading(true);
      try {
        const results = await searchPlaces(searchQ, { signal: ctrl.signal, limit: 6 });
        if (!ctrl.signal.aborted) setSearchResults(results);
      } catch (e) {
        if (e?.name !== 'AbortError') setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(id);
  }, [searchQ, searchOpen]);

  return {
    // state
    userLocation, locationLabel, locationSource,
    geolocating, geoError, radiusKm,
    searchOpen, searchQ, searchResults, searchLoading,
    recentPlaces,
    // setters / actions
    setRadiusKm,
    setSearchOpen, setSearchQ,
    requestLocation, clearLocation, pickResult, clearRecent, dismissError,
  };
}
